import React, { FC, Suspense, useCallback, useEffect, useMemo, useState } from 'react';

import { Box, OnClick } from '@rbilabs/components-library';
import delv from 'dlv';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { centsToDollarString } from 'utils';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { IBarcode } from 'components/barcode';
import { LazyComponent, lazyWithNullFallback } from 'components/layout/util';
import LoadPrepaidCardModal from 'components/load-prepaid-card-modal';
import SelectPaymentMethod from 'components/payment-method-select';
import Picture from 'components/picture';
import TextButton from 'components/text-button';
import { Tooltip } from 'components/tooltip';
import { Position } from 'components/tooltip/types';
import useDialogModal from 'hooks/use-dialog-modal';
import useEffectOnUnmount from 'hooks/use-effect-on-unmount';
import { useFeatureLayout } from 'hooks/use-feature-layout';
import useApplePay from 'state/apple-pay/hooks/use-apple-pay';
import { useAuthContext } from 'state/auth';
import {
  TransactionType,
  getGooglePaymentsConfigs,
} from 'state/google-pay/get-google-payments-configs';
import useGooglePay from 'state/google-pay/hooks/use-google-pay';
import { useNetworkContext } from 'state/network';
import { usePaymentContext } from 'state/payment';
import { PREPAID_RELOAD_AMOUNTS } from 'state/payment/constants';
import {
  CardTypes,
  IApplePayDetails,
  IGooglePayDetails,
  IPaymentMethod,
} from 'state/payment/types';
import { useUIContext } from 'state/ui';
import { getStoredPrepaidCard } from 'utils/encryption';
import { ISOs, getCountryAndCurrencyCodes } from 'utils/form/constants';
import logger from 'utils/logger';
import { isAllowedReloadMethod } from 'utils/payment';
import { isApplePay, isGooglePay } from 'utils/payment/native-payment';

const Barcode: LazyComponent<IBarcode> = lazyWithNullFallback(() => import('components/barcode'));

const NO_PREPAID_HEADING = 'No TimCard registered';
const INITIAL_LOAD_AMOUNT = 500;

type IActiveButton = {
  selected?: boolean;
};

interface IMargin {
  margin?: string;
}

interface ILoyalty {
  onDismiss: OnClick;
}

const MobileOnlyContent = styled.div`
  display: block;
  ${Styles.desktop`
    display: none;
  `}
  // it wouldn't display on Pixel phones when it was switched to Landscape mode
  // this ensures we still get the mobile content on pixel devices in Landscape mode
  @media only screen and (min-width: 820px) and (max-width: 828px) and (min-height: 400px) and (max-height: 415px) {
    display: block;
  }
`;

const DefaultContainer = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  padding: 2rem 0;
`;

const BarcodeContainer = styled.div`
  margin: 1.75rem 0 1.5rem;
  display: flex;
  align-items: center;
  flex-direction: column;
  flex-wrap: nowrap;
`;

const Heading = styled.h3`
  font-weight: ${Styles.fontWeight.heavy};
  font-size: 1.375rem;
  margin-bottom: 1rem;
  text-align: center;
`;

const NoConnectionHeading = styled(Heading)`
  display: none;
  ${Styles.desktop`
    display: unset;
  `}
`;

const SubHeading = styled.h5`
  font-size: 1.125rem;
  font-weight: 400;
  margin: 0;
  opacity: 0.5rem;
  text-align: center;
`;

const Separator = styled.div`
  border: solid 1px #000000;
  height: 2px;
  opacity: 0.2;
  margin: 0.5rem 0;
  width: 100%;
`;

const TimCardInfo = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  flex-wrap: nowrap;
  width: 100%;
`;

const TimCardTextImageContainer = styled.div`
  width: 77.5px;
`;

const ContentBlock = styled.div<IMargin>`
  display: flex;
  align-items: center;
  flex-direction: column;
  flex-wrap: nowrap;
  margin: ${p => (p.margin ? p.margin : '1rem 0')};
  width: 100%;
`;

const CardNumber = styled.span`
  color: ${Styles.color.white};
  margin-bottom: 0.5rem;
`;

const Balance = styled.span`
  color: ${Styles.color.white};
  display: flex;
  align-items: flex-start;
  margin-top: 0.5rem;
  padding-left: 40px;
`;

const CurrencySymbol = styled.span`
  color: ${Styles.color.white};
  font-size: 1.625rem;
  font-weight: ${Styles.fontWeight.heavy};
  margin-right: 0.25rem;
  position: relative;
  padding-left: 5px;
`;

const BalanceDollars = styled.span`
  font-size: 4.6875rem;
  line-height: 1;
  letter-spacing: -2px;
  margin-right: 0.2rem;
  position: relative;
`;

const BalanceCents = styled.span`
  font-size: 1.625rem;
  position: relative;
`;

const AmountContainer = styled.ul`
  display: flex;
  flex-direction: row;
  padding: 0;
  margin-top: 0;
  margin-bottom: 0.5rem;
  width: 100%;
  border-radius: 4px;
  height: 40px;
  border: 1px solid rgba(255, 255, 255, 0.5);
`;

const MoneyButton = styled.button<IActiveButton>`
  margin: 0;
  color: ${props => (props.selected ? Styles.color.primary : Styles.color.white)};
  background-color: ${props => (props.selected ? Styles.color.white : 'transparent')};
  border: none;
  cursor: pointer;
  font-weight: ${props => (props.selected ? 'bold' : 'normal')};
  font-size: 100%;
  flex: 1 1 auto;
  font-family: ${Styles.fontFamily.base};
  padding-top: 6px;
  max-width: 25%;
  &:not(:last-child) {
    border-right: ${props => (props.selected ? 'none' : '1px solid rgba(255, 255, 255, 0.5)')};
  }
  &:hover {
    background-color: ${props => (props.selected ? Styles.color.white : '#a5031d')};
    color: ${props => (props.selected ? Styles.color.primary : Styles.color.white)};
  }
  &:first-child {
    border-top-left-radius: 3px;
    border-bottom-left-radius: 3px;
  }
  &:last-child {
    border-top-right-radius: 3px;
    border-bottom-right-radius: 3px;
  }
  &:focus {
    outline: 1px solid ${Styles.color.white};
  }
  & > div {
    width: fit-content(100%);
    max-width: 100%;
    max-height: 100%;
  }
`;

const ActionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  justify-content: center;
  padding: 0.5rem;
`;

const FinalActionContainer = styled(ActionContainer)`
  padding-bottom: 150px;
`;

const InfoIcon = styled.div`
  min-width: 40px;
  min-height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: 0.2s;
  font-size: 1.1rem;
  cursor: default;
  font-weight: bold;
  margin-top: -4px;
  opacity: 0.5;

  &:hover {
    opacity: 0.8;
  }
`;

const SelectPaymentMethodWrapper = styled.div`
  height: 100%;
  width: 100%;
`;

const LoyaltyTimCard: FC<ILoyalty> = ({ onDismiss }) => {
  const { formatMessage } = useIntl();
  const {
    getPrepaidPaymentMethod,
    getBalanceFromPaymentMethods,
    paymentMethods,
    reloadPrepaidCard,
    loading,
    prepaidReloadPaymentMethodId: reloadPaymentMethodId,
    setPrepaidReloadPaymentMethodId: setReloadPaymentMethodId,
    openTransferBalanceDialog,
    getPaymentMethods,
    hasGetPaymentMethodsError,
  } = usePaymentContext();
  const auth = useAuthContext();
  const { currencySymbol, currencyIsSuffix, formatCurrencyForUser } = useUIContext();
  const billingCountry = delv(auth, 'user.details.isoCountryCode') || ISOs.CAN;
  const { countryCode, currencyCode } = getCountryAndCurrencyCodes(billingCountry);
  const { user } = useAuthContext();
  const { connection } = useNetworkContext();
  const [reloadAmt, setReloadAmt] = useState(user?.details?.defaultReloadAmt || 500);
  const { featureLayout } = useFeatureLayout();
  const timCardTextIconAsset = featureLayout?.giftCardTextImage?.locale;
  const navigate = useNavigate();

  // Load payment methods on unmount to avoid changing balance
  // while the user looks at it or pickup new balance if the
  // user just paid at the store
  useEffectOnUnmount(() => {
    getPaymentMethods();
  });

  const allowedReloadMethods = useMemo(() => paymentMethods.filter(isAllowedReloadMethod), [
    paymentMethods,
  ]);

  // Makes sure that saved reloadPaymentMethodId exists in currently saved reloadMethods
  const hasAvailableReloadMethod = useMemo(
    () =>
      allowedReloadMethods.find(
        ({ accountIdentifier, fdAccountId }) =>
          fdAccountId === reloadPaymentMethodId || accountIdentifier === reloadPaymentMethodId
      ),
    [allowedReloadMethods, reloadPaymentMethodId]
  );

  useEffect(() => {
    if (!allowedReloadMethods.length) {
      return;
    }

    // If no default reloadPaymentMethod available, set default to next available
    if (!hasAvailableReloadMethod) {
      const accountIdentifier =
        allowedReloadMethods[0].fdAccountId ?? (allowedReloadMethods[0].accountIdentifier || '');
      setReloadPaymentMethodId(accountIdentifier);
    }
  }, [allowedReloadMethods, hasAvailableReloadMethod, setReloadPaymentMethodId]);

  const [LoadPrepaidCardDialog, openLoadPrepaidCardDialog] = useDialogModal<
    {},
    { loadAmount: number }
  >({
    Component: LoadPrepaidCardModal,
  });

  const prepaidPaymentMethod = getPrepaidPaymentMethod();

  const applePaySelected = isApplePay(reloadPaymentMethodId);
  const googlePaySelected = isGooglePay(reloadPaymentMethodId);

  let dollars = '0';
  let cents = '00';
  let centsBalance = 0;

  if (prepaidPaymentMethod && prepaidPaymentMethod.prepaid) {
    centsBalance = getBalanceFromPaymentMethods(prepaidPaymentMethod!);
    const balance = centsToDollarString(centsBalance);
    [dollars, cents] = balance.split('.');
  }

  const reloadGiftCard = useCallback(
    (input?: IApplePayDetails | IGooglePayDetails) => {
      const giftCard = paymentMethods.find((method: IPaymentMethod) => !!method.prepaid);
      const paymentCard = paymentMethods.find(
        (method: IPaymentMethod) => method.fdAccountId === reloadPaymentMethodId
      );
      const prepaidFdAccountId = giftCard?.fdAccountId ?? null;
      const cardType = paymentCard?.credit?.cardType ?? null;
      const ccReloadDetails = {
        fdAccountId: reloadPaymentMethodId,
      };

      let args = {
        fundValue: reloadAmt,
        fdAccountId: reloadPaymentMethodId,
        prepaidBalance: centsBalance,
        countryCode,
        currencyCode,
        cardType,
        ...(prepaidFdAccountId && { prepaidFdAccountId }),
      };

      switch (reloadPaymentMethodId) {
        case CardTypes.APPLE_PAY:
          args = { ...args, ...{ applePayDetails: input } };
          break;
        case CardTypes.GOOGLE_PAY:
          args = { ...args, ...{ googlePayDetails: input } };
          break;
        default:
          args = { ...args, ...ccReloadDetails };
      }

      reloadPrepaidCard(args);
    },
    [
      centsBalance,
      countryCode,
      currencyCode,
      paymentMethods,
      reloadAmt,
      reloadPaymentMethodId,
      reloadPrepaidCard,
    ]
  );

  const handleApplePay = ({ error, data }: { error?: string; data?: IApplePayDetails }): void => {
    if (data) {
      reloadGiftCard(data);
      return;
    }
    if (error) {
      logger.warn(error);
    }
  };

  const { requestApplePayPayment } = useApplePay({
    handleApplePay,
    prepaid: true,
  });

  const { requestGooglePayPayment } = useGooglePay({
    onCompleted: (data: IGooglePayDetails) => {
      if (data) {
        reloadGiftCard(data);
      }
    },
  });

  const handleReloadClick = useCallback(() => {
    if (applePaySelected) {
      return requestApplePayPayment({
        countryCode,
        currencyCode,
        total: reloadAmt,
      });
    }
    if (googlePaySelected) {
      // Get the prepaid gateway configs for google pay
      const { gateway, gatewayMerchantId } = getGooglePaymentsConfigs({
        transactionType: TransactionType.PREPAID,
        countryCode,
      });
      return requestGooglePayPayment({
        countryCode,
        currencyCode,
        total: reloadAmt,
        gatewayMerchantId,
        gateway,
      });
    }
    return reloadGiftCard();
  }, [
    applePaySelected,
    googlePaySelected,
    reloadGiftCard,
    requestApplePayPayment,
    requestGooglePayPayment,
    countryCode,
    currencyCode,
    reloadAmt,
  ]);

  const handleNavigate = useCallback(
    (e, route) => {
      navigate(route, { replace: true });
      onDismiss(e);
    },
    [navigate, onDismiss]
  );

  const nonSelectedPaymentMethods = allowedReloadMethods.filter(
    (method: IPaymentMethod) => method.fdAccountId !== reloadPaymentMethodId
  );

  const selectedCreditMethod = allowedReloadMethods.find(
    (method: IPaymentMethod) => method.fdAccountId === reloadPaymentMethodId
  );

  let cardNumber = NO_PREPAID_HEADING;

  if (prepaidPaymentMethod && prepaidPaymentMethod.prepaid) {
    // add spaces between every 4 characters in the card number
    cardNumber = (
      prepaidPaymentMethod.prepaid.cardNumber.match(/.{1,4}/g) || [NO_PREPAID_HEADING]
    ).join(' ');
  }

  const reloadDisabled =
    !prepaidPaymentMethod ||
    !reloadAmt ||
    (!selectedCreditMethod && !applePaySelected && !googlePaySelected);

  const cachedPrepaidCard = getStoredPrepaidCard();

  const useLoadPrePaidDialog = !loading && !(prepaidPaymentMethod && prepaidPaymentMethod.prepaid);

  if (!connection.connected) {
    return (
      <>
        <NoConnectionHeading>
          {formatMessage({ id: 'unableToLoadTimCardNoInternet' })}
        </NoConnectionHeading>
        {cachedPrepaidCard && (
          <MobileOnlyContent>
            <ContentBlock>
              <Heading>{formatMessage({ id: 'scanToPayWithYourTimCard' })}</Heading>
              <SubHeading>{formatMessage({ id: 'putThisCodeUnderTheScanner' })}</SubHeading>
              <Suspense fallback={null}>
                <BarcodeContainer>
                  <Barcode value={cachedPrepaidCard} />
                  <Box margin="3rem 0.5rem 0.5rem">
                    <ActionButton variant={ActionButtonVariants.INVERSE} onClick={onDismiss}>
                      {formatMessage({ id: 'close' })}
                    </ActionButton>
                  </Box>
                </BarcodeContainer>
              </Suspense>
            </ContentBlock>
          </MobileOnlyContent>
        )}
      </>
    );
  }

  const CurrentCurrencySymbol = () => <CurrencySymbol>{currencySymbol}</CurrencySymbol>;

  return (
    <>
      <LoadPrepaidCardDialog loadAmount={INITIAL_LOAD_AMOUNT} />

      {useLoadPrePaidDialog ? (
        <DefaultContainer>
          <ContentBlock margin="6.5rem 0 0">
            <Heading>{formatMessage({ id: 'timCard' })}</Heading>
            <SubHeading>{formatMessage({ id: 'emptyStateTimCardCopy' })}</SubHeading>
            <ActionContainer>
              <ActionButton
                variant={ActionButtonVariants.INVERSE}
                fullWidth
                onClick={openLoadPrepaidCardDialog}
              >
                {formatMessage({ id: 'emptyStateTimCTACopy' })}
              </ActionButton>
              <TextButton onClick={openTransferBalanceDialog} color={Styles.color.white}>
                {formatMessage({ id: 'transferBalanceFromAnotherTimCard' })}
              </TextButton>
            </ActionContainer>
          </ContentBlock>
        </DefaultContainer>
      ) : (
        <>
          <ContentBlock margin="1.5rem 0 .5rem">
            <Heading>{formatMessage({ id: 'yourTimCardBalance' })}</Heading>
            {!hasGetPaymentMethodsError ? (
              <TimCardInfo>
                {timCardTextIconAsset && (
                  <TimCardTextImageContainer>
                    <Picture
                      image={timCardTextIconAsset}
                      alt={formatMessage({ id: 'timCard' })}
                      objectFitContain={true}
                    />
                  </TimCardTextImageContainer>
                )}
                <CardNumber>{cardNumber}</CardNumber>
                <Balance>
                  {!currencyIsSuffix && <CurrentCurrencySymbol />}
                  <BalanceDollars>{dollars}</BalanceDollars>
                  <BalanceCents>{cents}</BalanceCents>
                  {currencyIsSuffix && <CurrentCurrencySymbol />}
                  <Tooltip
                    text={formatMessage({ id: 'currencyInfo' })}
                    position={Position.LEFT}
                    showOnFocus={true}
                  >
                    <InfoIcon>ⓘ</InfoIcon>
                  </Tooltip>
                </Balance>
              </TimCardInfo>
            ) : (
              <BalanceDollars>-</BalanceDollars>
            )}
          </ContentBlock>

          <Separator />
          <MobileOnlyContent>
            <ContentBlock>
              <Heading>{formatMessage({ id: 'scanToPayWithYourTimCard' })}</Heading>
              <SubHeading>{formatMessage({ id: 'putThisCodeUnderTheScanner' })}</SubHeading>
              <Suspense fallback={null}>
                {prepaidPaymentMethod && prepaidPaymentMethod.prepaid && (
                  <BarcodeContainer>
                    <Barcode value={prepaidPaymentMethod.prepaid.shuffledCardNumber} />
                    <Box margin="3rem 0.5rem 0.5rem">
                      <ActionButton variant={ActionButtonVariants.INVERSE} onClick={onDismiss}>
                        {formatMessage({ id: 'close' })}
                      </ActionButton>
                    </Box>
                  </BarcodeContainer>
                )}
              </Suspense>
            </ContentBlock>

            <Separator />
          </MobileOnlyContent>

          <ContentBlock>
            <Heading>{formatMessage({ id: 'reloadYourCard' })}</Heading>
            {(!!allowedReloadMethods.length || selectedCreditMethod) && (
              <AmountContainer>
                {PREPAID_RELOAD_AMOUNTS.map((amount: number) => (
                  <MoneyButton
                    key={amount}
                    aria-label={formatMessage(
                      { id: 'reloadCardWithAmount' },
                      {
                        amount: formatCurrencyForUser(amount),
                      }
                    )}
                    selected={reloadAmt === amount}
                    onClick={() => setReloadAmt(amount)}
                    aria-current={reloadAmt === amount}
                  >
                    <div>{centsToDollarString(amount, 0)}</div>
                  </MoneyButton>
                ))}
              </AmountContainer>
            )}
            <SelectPaymentMethodWrapper>
              <SelectPaymentMethod
                selectedMethod={selectedCreditMethod}
                nonSelectedMethods={nonSelectedPaymentMethods}
                onOptionClick={setReloadPaymentMethodId}
                onNavigateClick={handleNavigate}
              />
            </SelectPaymentMethodWrapper>
            <FinalActionContainer>
              <ActionButton
                variant={ActionButtonVariants.INVERSE}
                fullWidth
                onClick={handleReloadClick}
                isLoading={loading}
                disabled={reloadDisabled}
                loadingColor={Styles.color.primary}
                skipLogEvent
              >
                {formatMessage(
                  { id: 'reloadCardWithAmount' },
                  {
                    amount: formatCurrencyForUser(reloadAmt),
                  }
                )}
              </ActionButton>
              <TextButton onClick={openTransferBalanceDialog} color={Styles.color.white}>
                {formatMessage({ id: 'transferBalanceFromAnotherTimCard' })}
              </TextButton>
            </FinalActionContainer>
          </ContentBlock>
        </>
      )}
    </>
  );
};

export default LoyaltyTimCard;
